/**
 * GWP 自动登录工具函数
 * 处理来自 GWP 系统的自动登录请求
 */

import { setStorage, clearStorage } from "./storage";
import { userFetch } from "@/apis/user";
import { User } from "@/types/user";
import { message } from "antd";
import { getNewToken } from "@/apis/token";

export interface GWPLoginData {
  accessToken: string;
  refreshToken: string;
}

/**
 * 处理 GWP 自动登录
 * @param accessToken - GWP 提供的访问令牌
 * @param refreshToken - GWP 提供的刷新令牌
 * @returns 用户信息
 */
export const handleGWPAutoLogin = async (
  accessToken: string,
  refreshToken: string
): Promise<User> => {
  try {
    // 清理之前的登录信息，确保账号切换时不会显示旧账号信息
    clearStorage();

    // 正确流程：仅使用 GWP 侧传来的 refreshToken 置换为本系统可用的 token
    // 1) 先保存 refreshToken
    setStorage("refreshToken", refreshToken, "locale");
    // 2) 调用续签接口，换取本系统 accessToken/refreshToken（内部已落盘）
    await getNewToken();

    console.log("[GWP Auto Login] 已通过 refreshToken 续签并保存 Token");

    // 获取用户信息以验证 token
    let user = await userFetch();

    // 后端当前返回的 mobile/name1/name2/areaCode 带有多余的引号, 这里清洗一次
    const unquote = (v: unknown) =>
      typeof v === "string" ? v.replace(/^"|"$/g, "") : v;
    user = {
      ...user,
      mobile: unquote(user.mobile) as string,
      name1: unquote(user.name1) as string,
      name2: unquote(user.name2) as string
      // 保留 areaCode 原枚举类型，避免类型冲突
    };

    if (!user.permissions || user.permissions.length === 0) {
      console.warn(
        "[GWP Auto Login] 用户权限为空，菜单将为空，请检查后端是否为原账号分配权限"
      );
    }
    if (!user.orgs || user.orgs.length === 0) {
      console.warn(
        "[GWP Auto Login] 用户组织列表为空，后续需补齐 org 才能正常使用业务功能"
      );
    }

    console.log("[GWP Auto Login] 用户信息获取成功:", user);

    return user;
  } catch (error) {
    console.error("[GWP Auto Login] 自动登录失败:", error);

    // 清除可能保存的无效 token
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");

    throw error;
  }
};

/**
 * 检查消息数据是否为有效的 GWP 登录数据
 */
export const isValidGWPLoginData = (data: any): data is GWPLoginData => {
  return (
    data &&
    typeof data === "object" &&
    typeof data.accessToken === "string" &&
    typeof data.refreshToken === "string" &&
    data.accessToken.length > 0 &&
    data.refreshToken.length > 0
  );
};

/**
 * 处理来自 GWP 的消息事件
 */
export const handleGWPMessage = async (
  event: MessageEvent,
  onSuccess: (user: User) => void,
  onError: (error: any) => void
): Promise<void> => {
  // 忽略 React DevTools / Webpack 自身的调试消息，减少控制台噪音
  // 这些消息通常包含 source 字段，可直接过滤
  const devtoolSources = [
    "react-devtools-content-script",
    "react-devtools-backend-manager",
    "react-devtools-bridge",
    "webpackWarnings"
  ];
  if (
    event?.data &&
    typeof event.data === "object" &&
    // 常见结构: { source: 'react-devtools-bridge', payload: {...} }
    (devtoolSources.includes((event.data as any).source) ||
      devtoolSources.includes((event.data as any).type))
  ) {
    return; // 直接忽略无关消息
  }

  // 2. 验证消息数据
  if (!isValidGWPLoginData(event.data)) {
    console.log("[GWP Auto Login] 收到的消息不是有效的登录数据");
    console.log("[GWP Auto Login] 消息来源:", event.origin);
    console.log("[GWP Auto Login] 收到的数据:", event.data);
    console.log(
      "[GWP Auto Login] 期望的数据格式: { accessToken: string, refreshToken: string }"
    );
    return;
  }

  console.log("[GWP Auto Login] 收到来自 GWP 的登录凭证");

  const { accessToken, refreshToken } = event.data;

  try {
    // 3. 执行自动登录
    const user = await handleGWPAutoLogin(accessToken, refreshToken);

    // 4. 调用成功回调
    onSuccess(user);

    // 5. 显示成功提示
    message.success("已从 GWP 自动登录");
  } catch (error) {
    // 6. 调用错误回调
    onError(error);

    // 7. 显示错误提示
    message.error("自动登录失败，请手动登录");
  }
};
